import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Phone, Mail } from 'lucide-react';
import storage from '../utils/storage';
import auth from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

// Extracted SupplierForm to top-level to avoid remount on parent re-renders (fixes input focus jump)
function SupplierForm({ data, setData }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Supplier Name *</Label>
        <Input
          data-testid="supplier-name-input"
          autoFocus
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          placeholder="Company name"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Contact Person</Label>
          <Input
            value={data.contact}
            onChange={(e) => setData({ ...data, contact: e.target.value })}
            placeholder="Contact name"
          />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input
            value={data.phone}
            onChange={(e) => setData({ ...data, phone: e.target.value })}
            placeholder="Phone number"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Email</Label>
        <Input
          type="email"
          value={data.email}
          onChange={(e) => setData({ ...data, email: e.target.value })}
          placeholder="email@example.com"
        />
      </div>
      <div className="space-y-2">
        <Label>Address</Label>
        <Textarea
          value={data.address}
          onChange={(e) => setData({ ...data, address: e.target.value })}
          placeholder="Complete address"
          rows={2}
        />
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={data.notes}
          onChange={(e) => setData({ ...data, notes: e.target.value })}
          placeholder="Additional notes"
          rows={3}
        />
      </div>
    </div>
  );
}

export default function SuppliersPage() {
  const user = auth.getCurrentUser();
  const [suppliers, setSuppliers] = useState(storage.getSuppliersForUser(user));
  const [products] = useState(storage.getProductsForUser(user));
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });
  
  // All users manage only their own suppliers. No roles.

  const resetForm = () => {
    setFormData({ name: '', contact: '', phone: '', email: '', address: '', notes: '' });
  };

  const handleAdd = () => {
    if (!formData.name) {
      toast.error('Please enter supplier name');
      return;
    }

    const newSupplier = {
      id: `sup-${Date.now()}`,
      ...formData,
      createdBy: user.id
    };

    const all = storage.getSuppliers();
    const updatedAll = [...all, newSupplier];
    storage.setSuppliers(updatedAll);
    setSuppliers(storage.getSuppliersForUser(user));
    toast.success('Supplier added successfully');
    setShowAddDialog(false);
    resetForm();
  };

  const handleEdit = () => {
    if (!formData.name) {
      toast.error('Please enter supplier name');
      return;
    }

    if (selectedSupplier.createdBy !== user.id) {
      toast.error("You don't have permission to edit this supplier");
      return;
    }

    const all = storage.getSuppliers();
    const updatedAll = all.map(s => 
      s.id === selectedSupplier.id ? { ...s, ...formData } : s
    );
    storage.setSuppliers(updatedAll);
    setSuppliers(storage.getSuppliersForUser(user));
    toast.success('Supplier updated successfully');
    setShowEditDialog(false);
    setSelectedSupplier(null);
  };

  const handleDelete = () => {
    if (selectedSupplier.createdBy !== user.id) {
      toast.error("You don't have permission to delete this supplier");
      setShowDeleteDialog(false);
      setSelectedSupplier(null);
      return;
    }

    const all = storage.getSuppliers();
    const updatedAll = all.filter(s => s.id !== selectedSupplier.id);
    storage.setSuppliers(updatedAll);
    setSuppliers(storage.getSuppliersForUser(user));

    // Clear supplier reference from products owned by this user that used this supplier
    const allProducts = storage.getProducts();
    const updatedProducts = allProducts.map(p => {
      if (p.createdBy === user.id && p.supplier === selectedSupplier.name) {
        return { ...p, supplier: '' };
      }
      return p;
    });
    storage.setProducts(updatedProducts);
    toast.success('Supplier deleted successfully');
    setShowDeleteDialog(false);
    setSelectedSupplier(null);
  };

  const getSupplierProductCount = (supplierName) => {
    return products.filter(p => p.supplier === supplierName).length;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tight">Suppliers</h1>
          <p className="text-muted-foreground mt-2">Manage your suppliers and vendors</p>
        </div>
        {(
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-supplier-btn" className="font-bold uppercase tracking-wide">
                <Plus size={20} className="mr-2" />
                Add Supplier
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] sm:w-full max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black font-heading">Add Supplier</DialogTitle>
                <DialogDescription>Add a new supplier or vendor</DialogDescription>
              </DialogHeader>
              <SupplierForm data={formData} setData={setFormData} />
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button data-testid="save-supplier-btn" onClick={handleAdd}>Save Supplier</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {suppliers.map(supplier => (
          <Card key={supplier.id} className="card-hover" data-testid={`supplier-card-${supplier.id}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users size={20} className="text-primary" />
                {supplier.name}
              </CardTitle>
              <CardDescription>{getSupplierProductCount(supplier.name)} products</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {supplier.contact && (
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Person</p>
                    <p className="font-medium">{supplier.contact}</p>
                  </div>
                )}
                {supplier.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-muted-foreground" />
                    <p className="text-sm">{supplier.phone}</p>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-muted-foreground" />
                    <p className="text-sm">{supplier.email}</p>
                  </div>
                )}
                {supplier.address && (
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="text-sm">{supplier.address}</p>
                  </div>
                )}
                {(supplier.createdBy === user.id) && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setSelectedSupplier(supplier);
                        setFormData({ ...supplier });
                        setShowEditDialog(true);
                      }}
                      data-testid={`edit-supplier-${supplier.id}`}
                    >
                      <Edit size={16} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-destructive hover:text-destructive"
                      onClick={() => {
                        setSelectedSupplier(supplier);
                        setShowDeleteDialog(true);
                      }}
                      data-testid={`delete-supplier-${supplier.id}`}
                    >
                      <Trash2 size={16} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="w-[95vw] sm:w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black font-heading">Edit Supplier</DialogTitle>
          </DialogHeader>
          <SupplierForm data={formData} setData={setFormData} />
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEdit}>Update Supplier</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedSupplier?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}